// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useEffect, useState } from 'react';

type ResolveConfirmation = (value: boolean) => void;

type ConfirmationOptions = {
    onProceed?: () => void;
};

class Confirmation {
    public isResolved = false;

    private onProceed?: ConfirmationOptions['onProceed'];

    constructor(
        private resolve: ResolveConfirmation,
        { onProceed }: ConfirmationOptions = {},
    ) {
        this.onProceed = onProceed;
    }

    proceed(isConfirmed: boolean) {
        this.isResolved = true;
        this.resolve(isConfirmed);
        this.onProceed?.();
    }
}

type UseConfirmationReturn = readonly [
    Confirmation | null,
    () => Promise<boolean>,
];

const useConfirmation = (): UseConfirmationReturn => {
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

    useEffect(() => () => {
        if (confirmation && !confirmation.isResolved) {
            confirmation.proceed(false);
        }
    }, [confirmation]);

    const triggerConfirmation = () => new Promise((resolve: ResolveConfirmation) => {
        const confirmation = new Confirmation(resolve, {
            onProceed: () => setConfirmation(null),
        });
        setConfirmation(confirmation);
    });

    return [
        confirmation,
        triggerConfirmation,
    ];
};

export default useConfirmation;
